import { useState, useEffect } from "react";
import { createAdminApiClient } from "../../lib/api";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { toast } from "sonner";
import { Eye, Package, CreditCard, Landmark, Building2 } from "lucide-react";

const STATUS_OPTIONS = [
    { value: "pending_payment", label: "Pending Payment", color: "bg-yellow-500" },
    { value: "pending", label: "Pending", color: "bg-yellow-500" },
    { value: "payment_verified", label: "Payment Verified & Form Signed", color: "bg-blue-500" },
    { value: "ready_for_dispatch", label: "Ready for Freight Dispatch", color: "bg-purple-500" },
    { value: "shipped", label: "Shipped", color: "bg-green-500" },
    { value: "cancelled", label: "Cancelled", color: "bg-red-500" },
];

const PAYMENT_ICONS = { wire: Landmark, financing: Building2, credit_card: CreditCard };
const PAYMENT_LABELS = { wire: "Bank Wire / ACH", financing: "TimePayment Financing", credit_card: "Credit Card (Wave)" };

const AdminOrders = () => {
    const [orders, setOrders] = useState([]);
    const [selected, setSelected] = useState(null);
    const [loading, setLoading] = useState(true);

    const getAdminApi = () => {
        const creds = JSON.parse(sessionStorage.getItem("adminCredentials") || "{}");
        return createAdminApiClient(creds.username, creds.password);
    };

    const fetchOrders = async () => {
        try {
            const api = getAdminApi();
            const res = await api.getOrders();
            setOrders(res.data);
        } catch (err) {
            toast.error("Failed to fetch orders");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchOrders(); }, []);

    const updateStatus = async (orderId, newStatus) => {
        try {
            const api = getAdminApi();
            await api.updateOrder(orderId, { status: newStatus });
            toast.success("Order status updated");
            fetchOrders();
            if (selected?.id === orderId) setSelected(prev => ({ ...prev, status: newStatus }));
        } catch (err) {
            toast.error("Failed to update status");
        }
    };

    const fmt = (n) => `$${(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;

    return (
        <div className="space-y-6" data-testid="admin-orders-page">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="font-heading text-2xl font-bold text-foreground">Orders</h1>
                    <p className="text-muted-foreground text-sm">{orders.length} orders total</p>
                </div>
            </div>

            {loading ? (
                <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-16 bg-card border border-border animate-pulse" />)}</div>
            ) : orders.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                    <Package className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>No orders yet</p>
                </div>
            ) : (
                <div className="space-y-2">
                    {orders.map(order => {
                        const PayIcon = PAYMENT_ICONS[order.payment_method] || Package;
                        const statusObj = STATUS_OPTIONS.find(s => s.value === order.status) || STATUS_OPTIONS[0];
                        return (
                            <div key={order.id} className="bg-card border border-border p-4 flex items-center gap-4 hover:border-primary/30 transition-colors" data-testid={`order-row-${order.id}`}>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className="font-mono font-bold text-foreground text-sm">{order.order_number}</span>
                                        <Badge className={`${statusObj.color} text-white text-[10px] rounded-none`}>{statusObj.label}</Badge>
                                    </div>
                                    <p className="text-muted-foreground text-xs truncate">
                                        {order.shipping?.first_name} {order.shipping?.last_name} {order.shipping?.company ? `(${order.shipping.company})` : ""}
                                    </p>
                                </div>
                                <div className="text-right shrink-0">
                                    <p className="font-mono text-primary font-bold">{fmt(order.total)}</p>
                                    <div className="flex items-center gap-1 text-muted-foreground text-xs justify-end">
                                        <PayIcon className="h-3 w-3" />
                                        {PAYMENT_LABELS[order.payment_method] || order.payment_method}
                                    </div>
                                </div>
                                <div className="shrink-0 flex items-center gap-2">
                                    <select
                                        value={order.status}
                                        onChange={(e) => updateStatus(order.id, e.target.value)}
                                        className="bg-input border-transparent text-xs h-8 px-2 rounded-none"
                                        data-testid={`status-select-${order.id}`}
                                    >
                                        {STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                                    </select>
                                    <Button variant="ghost" size="icon" onClick={() => setSelected(order)} data-testid={`view-order-${order.id}`}>
                                        <Eye className="h-4 w-4" />
                                    </Button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Order Detail Dialog */}
            <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
                <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto bg-background border-border rounded-none">
                    <DialogHeader>
                        <DialogTitle className="font-heading">Order {selected?.order_number}</DialogTitle>
                    </DialogHeader>
                    {selected && (
                        <div className="space-y-6 text-sm">
                            {/* Status */}
                            <div className="flex items-center gap-3">
                                <span className="text-muted-foreground">Status:</span>
                                <select
                                    value={selected.status}
                                    onChange={(e) => updateStatus(selected.id, e.target.value)}
                                    className="bg-input border-transparent text-sm h-9 px-3 rounded-none"
                                >
                                    {STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                                </select>
                            </div>

                            {/* Customer */}
                            <div className="bg-muted/20 border border-border p-4">
                                <h3 className="font-bold text-foreground mb-2">Customer & Delivery Address</h3>
                                <p>{selected.shipping?.first_name} {selected.shipping?.last_name}</p>
                                {selected.shipping?.company && <p>{selected.shipping.company}</p>}
                                <p>{selected.shipping?.address}</p>
                                <p>{selected.shipping?.city}, {selected.shipping?.state} {selected.shipping?.zip_code}</p>
                                <p>Phone: {selected.shipping?.phone}</p>
                                <p>Email: {selected.shipping?.email}</p>
                            </div>

                            {/* Items */}
                            <div>
                                <h3 className="font-bold text-foreground mb-2">Items</h3>
                                {selected.items?.map((item, i) => (
                                    <div key={i} className="flex justify-between py-2 border-b border-border">
                                        <span>{item.product_name} × {item.quantity}</span>
                                        <span className="font-mono font-bold">{fmt(item.price)}</span>
                                    </div>
                                ))}
                            </div>

                            {/* Totals */}
                            <div className="space-y-1 text-right">
                                <p>Subtotal: <span className="font-bold">{fmt(selected.subtotal)}</span></p>
                                <p>Shipping: <span className="font-bold">{fmt(selected.shipping_cost)}</span></p>
                                {selected.credit_card_fee > 0 && <p>CC Fee (3.5%): <span className="font-bold">{fmt(selected.credit_card_fee)}</span></p>}
                                <p className="text-lg text-primary font-bold">Total: {fmt(selected.total)}</p>
                            </div>

                            {/* Payment Details */}
                            <div className="bg-muted/20 border border-border p-4">
                                <h3 className="font-bold text-foreground mb-2">Payment: {PAYMENT_LABELS[selected.payment_method]}</h3>
                                {selected.payment_method === "credit_card" && selected.card_splits && (
                                    <div className="space-y-1">
                                        <p className="text-muted-foreground text-xs mb-2">Card Split Details:</p>
                                        {selected.card_splits.map((split, i) => (
                                            <p key={i}>Card {i + 1}: Base {fmt(split.amount)} → Charged {fmt(split.charged)} (incl. 3.5%)</p>
                                        ))}
                                    </div>
                                )}
                                {selected.payment_method === "wire" && (
                                    <p className="text-muted-foreground">Wire transfer — awaiting receipt</p>
                                )}
                                {selected.payment_method === "financing" && (
                                    <p className="text-muted-foreground">TimePayment financing application submitted</p>
                                )}
                            </div>

                            <p className="text-muted-foreground text-xs">Created: {new Date(selected.created_at).toLocaleString()}</p>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default AdminOrders;
